import { Building2, Check, ChevronsUpDown } from "lucide-react";
import { useOrganization } from "@/contexts/OrganizationContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface Props {
  collapsed?: boolean;
  /**
   * Gate switching organizations behind the app-wide unsaved-changes guard —
   * e.g. a dirty dashboard draft shouldn't silently get saved into (or
   * abandoned for) a different org. Defaults to a no-op that always allows
   * the switch, so this component works standalone outside AppShell.
   */
  confirmNavigate?: () => boolean;
}

/**
 * Org switcher — only renders if the user belongs to more than one org.
 * Placed in the AppShell sidebar below the brand mark.
 */
export function OrgSwitcher({ collapsed = false, confirmNavigate = () => true }: Props) {
  const { memberships, organization, setActiveOrgId } = useOrganization();

  // Only show when user has multiple orgs
  if (memberships.length <= 1) return null;

  const handleSelect = (orgId: string) => {
    // Cancelling must leave the current org selected — do not switch.
    if (!confirmNavigate()) return;
    setActiveOrgId(orgId);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "w-full justify-between gap-2 px-2 py-1.5 text-sm font-medium",
            collapsed && "justify-center px-1",
          )}
        >
          <span className="flex items-center gap-2 truncate">
            <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            {!collapsed && (
              <span className="truncate">{organization?.name ?? "Select org"}</span>
            )}
          </span>
          {!collapsed && (
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
          Switch organization
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {memberships.map((m) => (
          <DropdownMenuItem
            key={m.organization_id}
            onClick={() => handleSelect(m.organization_id)}
            className="flex items-center gap-2"
          >
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <span className="flex-1 truncate">{m.organization.name}</span>
            {m.organization_id === organization?.id && (
              <Check className="h-4 w-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
